import { Injectable } from '@angular/core';
import { limit, query, where } from '@angular/fire/firestore';
import { ReplicationSyncStatus } from '@shared/models/replication-sync-status';
import { convertToDate } from '@shared/helpers/convert-to-date';
import { CashRegisterStatus } from 'src/app/point-of-sale/enums/cash-register-status';
import { CashRegisterStatusLog } from 'src/app/point-of-sale/models/cash-register-status-log';
import { BaseReplicableService } from './base-replicable.service';

/** The most values a Firestore `in` clause accepts. */
const FIRESTORE_IN_LIMIT = 30;

/**
 * Replicable service for cash register status logs (register open/close
 * events). Opts into the generic replicate → sync → recover pipeline:
 * every open/close log is written to
 * Firestore (the offline-first source of truth), mirrored to the Desktop store,
 * pushed to the backend by the status-log sync service, and restored from the
 * Desktop on recovery.
 */
@Injectable({
  providedIn: 'root'
})
export class CashRegisterStatusLogReplicationService extends BaseReplicableService<CashRegisterStatusLog> {
  protected readonly entityType = 'cashRegisterStatusLog';
  protected readonly endpoint = 'CashRegisterStatusLogs';
  protected readonly firestoreCollection = 'cashRegisterStatusLogs';

  protected override readonly replicationEnabled = true;
  protected override readonly defaultSyncStatus = ReplicationSyncStatus.UnSynced;

  // syncMetadata is for native service sync tracking only — strip from Firestore
  // copy. The backend-sync DTO re-derives it from metadata.localId.
  protected override async saveToFirestore(entity: CashRegisterStatusLog): Promise<void> {
    if (entity.openingDetails?.syncMetadata) {
      const { syncMetadata, ...rest } = entity.openingDetails;
      entity.openingDetails = rest;
    }
    if (entity.closingDetails?.syncMetadata) {
      const { syncMetadata, ...rest } = entity.closingDetails;
      entity.closingDetails = rest;
    }
    await super.saveToFirestore(entity);
  }

  /**
   * Fetch status logs that still need to be pushed to the backend. Scoped to
   * the current tenant and user, oldest first so the backend receives them in
   * change order.
   */
  async getUnsyncedAsync(maxRecords = 500): Promise<CashRegisterStatusLog[]> {
    return this.queryByStatus(ReplicationSyncStatus.UnSynced, maxRecords);
  }

  /**
   * Status logs the backend rejected on an earlier cycle. They stay local and
   * are retried like `UnSynced` ones — `Failed` records the reason, it does not
   * park the record.
   */
  async getFailedAsync(maxRecords = 500): Promise<CashRegisterStatusLog[]> {
    return this.queryByStatus(ReplicationSyncStatus.Failed, maxRecords);
  }

  /**
   * Recover stale `Syncing` records left behind by an interrupted push (e.g. a
   * crash between "mark Syncing" and the backend response). They are treated as
   * unsynced so the next cycle retries them.
   */
  async getStaleSyncingAsync(olderThanMs = 5 * 60 * 1000, maxRecords = 500): Promise<CashRegisterStatusLog[]> {
    const all = await this.queryByStatus(ReplicationSyncStatus.Syncing, maxRecords);
    const threshold = Date.now() - olderThanMs;
    return all.filter(log => {
      const stamp = log.metadata?.updatedDate ?? log.metadata?.createdDate;
      return !stamp || this.toMillis(stamp) < threshold;
    });
  }

  async getByLocalIdAsync(localId: string): Promise<CashRegisterStatusLog | undefined> {
    return this.getFromFirestore(localId);
  }

  /**
   * The CLOSED status logs of a cash register for the given closure codes —
   * the posting-status lookup the available-to-sell dialog makes, read from
   * Firestore (the local source of truth), fully Desktop-independent.
   *
   * Every filter is part of the query (equalities only, so no composite index
   * is required): the register's whole open/close history is never downloaded
   * to pick a few closures out of it. Firestore caps an `in` clause at
   * {@link FIRESTORE_IN_LIMIT} values, so the codes are queried in chunks.
   */
  async getClosuresByCodesAsync(
    cashRegisterId: string | undefined,
    closureCodes: string[]
  ): Promise<CashRegisterStatusLog[]> {
    if (!cashRegisterId || !closureCodes?.length) return [];

    const tenantId = this.authService.currentTenant;
    const logs: CashRegisterStatusLog[] = [];
    for (let i = 0; i < closureCodes.length; i += FIRESTORE_IN_LIMIT) {
      const queryRef = query(
        this.firestoreService.col<CashRegisterStatusLog>(this.firestoreCollection),
        where('metadata.organizationInfo.id', '==', tenantId),
        where('cashRegister.id', '==', cashRegisterId),
        where('status', '==', CashRegisterStatus.Closed),
        where('closingDetails.code', 'in', closureCodes.slice(i, i + FIRESTORE_IN_LIMIT))
      );
      logs.push(...await this.firestoreService.getCollectionAsync<CashRegisterStatusLog>(queryRef));
    }
    return logs;
  }

  /**
   * Persist a record recovered from the Desktop into Firestore without
   * re-emitting a replication event (it already exists on the Desktop).
   */
  async restoreToFirestore(entity: CashRegisterStatusLog): Promise<void> {
    await this.saveToFirestore(entity);
  }

  private async queryByStatus(
    status: ReplicationSyncStatus,
    maxRecords: number
  ): Promise<CashRegisterStatusLog[]> {
    const tenantId = this.authService.currentTenant;
    const userEmail = this.authService.userProfile?.email;
    // Equality-only query (no orderBy) so Firestore can serve it from
    // single-field indexes without a deployed composite index. Records are
    // sorted by creation order client-side — batches are bounded by `limit`.
    const queryRef = query(
      this.firestoreService.col<CashRegisterStatusLog>(this.firestoreCollection),
      where('metadata.organizationInfo.id', '==', tenantId),
      where('metadata.createdBy.email', '==', userEmail),
      where('metadata.syncInfo.syncStatus', '==', status),
      limit(maxRecords)
    );
    const results = await this.firestoreService.getCollectionAsync<CashRegisterStatusLog>(queryRef);
    return results.sort(
      (a, b) => this.toMillis(a.metadata?.createdDate) - this.toMillis(b.metadata?.createdDate)
    );
  }

  /**
   * Normalize a date that may be a Firestore Timestamp, an ISO string, or a
   * Date into epoch milliseconds (0 when absent/invalid).
   */
  private toMillis(value: unknown): number {
    if (!value) return 0;
    const date = convertToDate(value as any);
    const time = date instanceof Date ? date.getTime() : NaN;
    return isNaN(time) ? 0 : time;
  }
}
